import React from 'react';
import SEOHead from '../components/SEOHead';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import CloudIcon from '@mui/icons-material/Cloud';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ShareIcon from '@mui/icons-material/Share';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import UpdateIcon from '@mui/icons-material/Update';

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

const CaloriqPrivacy = () => {
  const theme = useTheme();
  const lastUpdated = "January 2026";

  const sections = [
    {
      title: "Information We Collect",
      icon: <InfoIcon />,
      subsections: [
        {
          subtitle: "Information You Provide",
          content: [
            "Manual food entries (food name, calories, quantities)",
            "Daily calorie goals",
            "Optional food images (only when you choose AI food analysis)",
            "Optional nutrition label images",
            "Optional text input (food descriptions)",
            "Manual entries are stored locally on your device by default."
          ]
        },
        {
          subtitle: "Information Collected Automatically",
          content: [
            "App version",
            "Device type",
            "Crash logs (for app stability)",
            "This data does not personally identify you."
          ]
        }
      ]
    },
    {
      title: "How We Use Your Information",
      icon: <SecurityIcon />,
      content: [
        "Provide calorie tracking features",
        "Estimate calories using AI (when requested)",
        "Store and display your food logs",
        "Improve app performance and reliability",
        "Process in-app purchases",
        "We do NOT use your data for advertising."
      ]
    },
    {
      title: "AI Features & Image Processing",
      icon: <CameraAltIcon />,
      content: [
        "When you use AI features (such as food photo analysis or label scanning):",
        "Images and related text are securely sent to our servers",
        "Images are processed to estimate calorie values",
        "Results are returned to your device",
        "AI processing is user-initiated only and optional.",
        "We do NOT use your images to train AI models."
      ]
    },
    {
      title: "Data Storage",
      icon: <CloudIcon />,
      subsections: [
        {
          subtitle: "Local Storage (Default)",
          content: [
            "Manual food logs",
            "Daily calorie goals",
            "Stored only on your device."
          ]
        },
        {
          subtitle: "Cloud Storage (Paid Feature)",
          content: [
            "If you choose to enable cloud sync:",
            "Food logs and settings may be stored securely",
            "This allows backup and multi-device access",
            "Cloud sync is optional and disabled by default."
          ]
        }
      ]
    },
    {
      title: "Data Sharing",
      icon: <ShareIcon />,
      content: [
        "We do NOT sell or share your personal data.",
        "We may share limited data with:",
        "Service providers (e.g., AI processing, payments)",
        "Only to provide app functionality",
        "All providers are required to protect your data."
      ]
    },
    {
      title: "Data Retention",
      icon: <UpdateIcon />,
      content: [
        "Local data remains on your device until you delete it",
        "Cloud data is retained until you delete your account or data",
        "Images used for AI analysis are not stored longer than necessary"
      ]
    },
    {
      title: "Your Rights (GDPR)",
      icon: <ContactMailIcon />,
      content: [
        "If you are in the EU, you have the right to:",
        "Access your data",
        "Delete your data",
        "Withdraw consent for AI features",
        "Request data export (where applicable)",
        "You can delete your data at any time from within the app."
      ]
    },
    {
      title: "Data Security",
      icon: <SecurityIcon />,
      content: [
        "We use reasonable technical and organizational measures to protect your data, including:",
        "Secure communication",
        "Access controls",
        "Minimal data collection",
        "No system is 100% secure, but we work to protect your information."
      ]
    },
    {
      title: "Children's Privacy",
      icon: <ChildCareIcon />,
      content: [
        "Caloriq is NOT intended for children under 13.",
        "We do not knowingly collect personal data from children."
      ]
    },
    {
      title: "Health Disclaimer",
      icon: <HealthAndSafetyIcon />,
      content: [
        "Caloriq provides calorie estimates for informational purposes only.",
        "It does not provide medical, dietary, or health advice.",
        "Always consult a qualified professional for medical or dietary guidance."
      ]
    }
  ];

  return (
    <>
      <SEOHead
        title="Privacy Policy - Caloriq AI Calorie Tracker"
        description="Privacy Policy for Caloriq AI Calorie Tracker - Learn how we collect, use, and protect your personal information. GDPR compliant."
        keywords="Caloriq privacy policy, AI calorie tracker, data protection, GDPR, privacy rights, personal information protection, food tracking app privacy"
        url="/privacy/caloriq"
        type="website"
        schemaType="WebPage"
        noindex={true}
      />

      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 10 }}>
        <Container maxWidth="lg">
          {/* Header */}
          <MotionBox
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', mb: 6 }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Caloriq Privacy Policy
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: 'auto', mb: 1 }}
            >
              AI Calorie Tracker
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 700, mx: 'auto', mb: 2 }}
            >
              We respect your privacy and are committed to protecting it. This Privacy Policy explains what data we collect, how it is used, and your rights.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {lastUpdated}
            </Typography>
          </MotionBox>

          {/* Introduction */}
          <MotionPaper
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              Caloriq ("the App") is developed and operated by <strong>DevHopesLabs</strong> ("we", "our", "us"). 
              We respect your privacy and are committed to protecting it.
            </Typography>
          </MotionPaper>

          {/* Sections */}
          {sections.map((section, index) => (
            <MotionPaper
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 * (index + 3) }}
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  p: 3,
                  bgcolor: 'primary.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box sx={{ color: 'primary.main' }}>
                  {section.icon}
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {section.title}
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                {section.subsections ? (
                  section.subsections.map((subsection, subIndex) => (
                    <Box key={subIndex} sx={{ mb: subIndex < section.subsections.length - 1 ? 3 : 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                        {subsection.subtitle}
                      </Typography>
                      <List sx={{ p: 0 }}>
                        {subsection.content.map((item, itemIndex) => (
                          <ListItem key={itemIndex} sx={{ px: 0, py: 1 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  bgcolor: 'primary.main',
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={item}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  fontSize: '1rem',
                                  lineHeight: 1.7,
                                  color: 'text.primary',
                                },
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ))
                ) : (
                  <List sx={{ p: 0 }}>
                    {section.content.map((item, itemIndex) => (
                      <ListItem key={itemIndex} sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={item}
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '1rem',
                              lineHeight: 1.7,
                              color: 'text.primary',
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </MotionPaper>
          ))}

          {/* Changes to Policy */}
          <MotionPaper
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            elevation={0}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ color: 'primary.main' }}>
                <UpdateIcon />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                Changes to This Policy
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
              We may update this Privacy Policy from time to time. Changes will be posted within the app or on our website.
            </Typography>
          </MotionPaper>

          {/* Contact Information */}
          <MotionPaper
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            elevation={0}
            sx={{
              p: 4,
              mb: 6,
              borderRadius: 3,
              border: '2px solid',
              borderColor: 'primary.main',
              bgcolor: 'primary.50',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
              Contact Us
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 2 }}>
              If you have questions or concerns about this Privacy Policy:
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Company:</strong> DevHopesLabs
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>Email:</strong> info@devhopeslabs.com
              </Typography>
              <Typography variant="body1">
                <strong>Website:</strong> https://devhopeslabs.com
              </Typography>
            </Box>
          </MotionPaper>
        </Container>
      </Box>
    </>
  );
};

export default CaloriqPrivacy;
